import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import { Span } from 'common/src/designSystem/components/span';
import {
    CheckInStrategy,
    PositionsSlotsUsersInfoId,
    UserPositionSlotUserInfoFragment
} from 'common/src/generated/types';
import { IntervalService } from 'common/src/services/intervalService';
import { useService } from 'common/src/util/dependencies/dependencies';
import { Emptyable } from 'common/src/util/emptyable';
import { getLocalZone } from 'common/src/util/luxon';
import { fullName } from 'common/src/vo/positionSlot';
import { produce } from 'immer';
import { DateTime } from 'luxon';
import * as React from 'react';
import { Button } from '../../designSystem/components/button';
import { DatetimeInput } from '../../designSystem/components/date/datetimeInput';
import { Modal } from '../../designSystem/components/modal/modal';
import { TextInput } from '../../designSystem/components/textInput';
import { usePositionsSlotsUsersInfosCheckInMutation } from '../../generated/graphqlHooks';
import { useHeavent } from '../../hooks/useHeavent';

interface IUpdateAllCheckInModalProps {
    isOpen: boolean;
    assignments: UserPositionSlotUserInfoFragment[];

    onClose(): void;
    onSuccess(): void;
}

export const UpdateAllCheckInModal = (props: IUpdateAllCheckInModalProps) => {
    const {
        translate,
        params: { eventId }
    } = useHeavent();
    const intervalService = useService(IntervalService);
    const { mutate: checkIn } = usePositionsSlotsUsersInfosCheckInMutation();
    const zone = React.useMemo(() => getLocalZone(), []);
    const [psuis, setPsuis] = React.useState(
        props.assignments.map((psui) => ({
            ...psui,
            checkInAt: psui.checkInAt?.toLocal(),
            checkOutAt: psui.checkOutAt?.toLocal()
        }))
    );
    const isValid = React.useMemo(
        () =>
            psuis.every(
                (psui) =>
                    (psui.checkInAt?.isValid &&
                        psui.checkOutAt?.isValid &&
                        psui.checkOutAt > psui.checkInAt) ||
                    (psui.checkInAt?.isValid && !psui.checkOutAt?.isValid) ||
                    (!psui.checkInAt?.isValid && !psui.checkOutAt?.isValid)
            ),
        [psuis]
    );
    const updateValue = React.useCallback(
        (
            id: PositionsSlotsUsersInfoId,
            key: 'breakTime' | 'checkInAt' | 'checkOutAt',
            newValue: number | Emptyable<DateTime>
        ) => {
            setPsuis(
                produce((currentPsuis) => {
                    currentPsuis.forEach((psui) => {
                        if (psui.id === id) {
                            psui[key] = (newValue || undefined) as any;
                        }
                    });
                })
            );
        },
        [psuis, setPsuis]
    );
    const onUpdate = async () => {
        await Promise.all(
            psuis.map((psui) =>
                checkIn({
                    eventId,
                    checkIn: {
                        strategy: CheckInStrategy.CheckInOut,
                        breakTime: psui.breakTime,
                        checkInAt: psui.checkInAt,
                        checkOutAt: psui.checkOutAt,
                        positionsSlotsUsersInfosIds: [psui.id]
                    }
                })
            )
        );

        props.onClose();
        props.onSuccess();
    };

    return (
        <Modal.Root isOpen={props.isOpen} onClose={props.onClose}>
            <Modal.Portal>
                <Modal.Header icon="sliders">{translate('mettre_jour_d_54198')}</Modal.Header>

                <Modal.Content gap="4">
                    {psuis.map((psui) => (
                        <Flex key={psui.id} direction="column" gap="1">
                            <Box font="gray800 textSm medium">
                                {psui.position.name} -{' '}
                                <Span fontWeight="regular">
                                    {fullName(intervalService, psui.positionSlot, '', {
                                        slot: { includeRange: true }
                                    })}
                                </Span>
                            </Box>

                            <Flex gap="6">
                                <DatetimeInput
                                    rightIcon="xmark"
                                    value={psui.checkInAt}
                                    zone={zone}
                                    onChange={(newValue) => {
                                        updateValue(psui.id, 'checkInAt', newValue);
                                    }}
                                    onRightIconClick={() => {
                                        updateValue(psui.id, 'checkInAt', null);
                                    }}
                                />

                                <DatetimeInput
                                    rightIcon="xmark"
                                    value={psui.checkOutAt}
                                    zone={zone}
                                    onChange={(newValue) => {
                                        updateValue(psui.id, 'checkOutAt', newValue);
                                    }}
                                    onRightIconClick={() => {
                                        updateValue(psui.id, 'checkOutAt', null);
                                    }}
                                />

                                <TextInput
                                    shouldParseAsInt={true}
                                    value={psui.breakTime}
                                    onChange={(newValue) => {
                                        updateValue(psui.id, 'breakTime', newValue);
                                    }}
                                />
                            </Flex>
                        </Flex>
                    ))}
                </Modal.Content>

                <Modal.Buttons>
                    <Button disabled={!isValid} onClick={onUpdate}>
                        {translate('mettre_jour_85141')}
                    </Button>
                </Modal.Buttons>
            </Modal.Portal>
        </Modal.Root>
    );
};
